import { z } from 'zod'

/**
 * FROZEN CONSTANTS — S360app depends on these exact string values.
 *
 * THESE VALUES ARE FROZEN — Do NOT add, modify, or create alternative values.
 * Do NOT use variations (e.g., 'completed' instead of 'complete').
 * Do NOT use different casing (e.g., 'Active' instead of 'active').
 *
 * All D1 writes to shared tables must validate against these enums.
 * Zod will reject any value not in these lists at runtime.
 *
 * Valid actions: login, survey_submitted, content_submitted, signoff_completed,
 *   invoice_generated, invoice_paid, change_request_submitted, approval_sent,
 *   design_approved, module_activated, blog_published, newsletter_sent,
 *   password_changed, profile_updated, perk_offered, perk_withdrawn,
 *   endorsement_created, referral_created
 *
 * Valid actors: client, steve, system
 *
 * Valid module statuses: inactive, active, in_progress, complete
 *
 * Valid module names: survey, approval, content, newsletter, signoff
 *
 * Valid invoice statuses: unpaid, paid, overdue
 *
 * Valid blog post statuses: draft, in_review, approved, publishing, published, archived
 *
 * Valid newsletter statuses: pending, approved, rejected, scheduled, sending, sent, failed
 *
 * Valid perk statuses: pending, approved, active, expired, withdrawn
 *
 * Valid referral statuses: pending, qualified, credited
 *
 * Valid recommendation statuses: pending, approved, rejected
 *
 * Valid change request statuses: submitted, in_progress, completed
 */

export const ActivityAction = z.enum([
  'login',
  'survey_submitted',
  'content_submitted',
  'signoff_completed',
  'invoice_generated',
  'invoice_paid',
  'change_request_submitted',
  'approval_sent',
  'design_approved',
  'module_activated',
  'blog_published',
  'newsletter_sent',
  'password_changed',
  'profile_updated',
  'perk_offered',
  'perk_withdrawn',
  'endorsement_created',
  'referral_created',
])
export type ActivityActionType = z.infer<typeof ActivityAction>

export const ActivityActor = z.enum(['client', 'steve', 'system'])
export type ActivityActorType = z.infer<typeof ActivityActor>

export const ModuleStatusValue = z.enum(['inactive', 'active', 'in_progress', 'complete'])
export type ModuleStatusValueType = z.infer<typeof ModuleStatusValue>

export const ModuleName = z.enum(['survey', 'approval', 'content', 'newsletter', 'signoff'])
export type ModuleNameType = z.infer<typeof ModuleName>

export const InvoiceStatus = z.enum(['unpaid', 'paid', 'overdue'])
export type InvoiceStatusType = z.infer<typeof InvoiceStatus>

export const BlogPostStatus = z.enum(['draft', 'in_review', 'approved', 'publishing', 'published', 'archived'])
export type BlogPostStatusType = z.infer<typeof BlogPostStatus>

export const NewsletterStatus = z.enum(['pending', 'approved', 'rejected', 'scheduled', 'sending', 'sent', 'failed'])
export type NewsletterStatusType = z.infer<typeof NewsletterStatus>

export const PerkStatus = z.enum(['pending', 'approved', 'active', 'expired', 'withdrawn'])
export type PerkStatusType = z.infer<typeof PerkStatus>

export const ReferralStatus = z.enum(['pending', 'qualified', 'credited'])
export type ReferralStatusType = z.infer<typeof ReferralStatus>

export const RecommendationStatus = z.enum(['pending', 'approved', 'rejected'])
export type RecommendationStatusType = z.infer<typeof RecommendationStatus>

export const ChangeRequestStatus = z.enum(['submitted', 'in_progress', 'completed'])
export type ChangeRequestStatusType = z.infer<typeof ChangeRequestStatus>
